/**
 * Saved Start for me prompts (localStorage, V1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Mic, PenLine, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { START_FOR_ME_COPY } from '../../content/memoryPromptLibrary';
import { getSavedPrompts, removeSavedPrompt } from '../../utils/startForMeStorage';
import { handoffRecordVoice, handoffWriteNow } from '../../utils/startForMeActions';

export function SavedPromptsSection({ personId = null, refreshKey = 0 }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);

  const reload = useCallback(() => {
    setSaved(getSavedPrompts());
  }, []);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  const handleRemove = (id) => {
    removeSavedPrompt(id);
    reload();
  };

  const toPrompt = (entry) => ({
    id: entry.promptId,
    mainPrompt: entry.mainPrompt,
    followUps: entry.followUps,
    suggestedChapter: entry.suggestedChapter,
  });

  return (
    <section
      className="rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-[hsl(42_28%_99%)] p-5 sm:p-6"
      data-testid="start-for-me-saved"
    >
      <h2 className="font-serif text-lg text-[#1c1917]">{START_FOR_ME_COPY.savedTitle}</h2>
      {saved.length === 0 ? (
        <p className="mt-3 text-sm leading-relaxed text-[#5c534a]">{START_FOR_ME_COPY.savedEmpty}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {saved.map((entry) => {
            const effectivePersonId = entry.personId || personId;
            const prompt = toPrompt(entry);
            return (
              <li
                key={entry.id}
                className="rounded-xl border border-[hsl(38_14%_86%)]/70 bg-white/60 px-4 py-3.5"
              >
                <p className="font-serif text-[0.95rem] leading-relaxed text-[#1c1917]">{entry.mainPrompt}</p>
                {entry.suggestedChapter ? (
                  <p className="mt-1 text-xs text-[#7a7268]">
                    {START_FOR_ME_COPY.suggestedChapter}: {entry.suggestedChapter}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-full bg-[#8B7355] px-3 text-xs hover:bg-[#7A6548]"
                    onClick={() => handoffWriteNow(navigate, effectivePersonId, prompt)}
                  >
                    <PenLine className="mr-1 h-3 w-3" aria-hidden />
                    {START_FOR_ME_COPY.writeNow}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full px-3 text-xs"
                    onClick={() => handoffRecordVoice(navigate, effectivePersonId, prompt)}
                  >
                    <Mic className="mr-1 h-3 w-3" aria-hidden />
                    {START_FOR_ME_COPY.recordVoice}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-full px-2 text-xs text-muted-foreground"
                    onClick={() => handleRemove(entry.id)}
                    aria-label="Remove saved prompt"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default SavedPromptsSection;
